export type CloudAutoStartInput = {
  loading: boolean;
  appView: string;
  hasWorkspace: boolean;
  selectedAgent: string | null;
  agentStatus: string;
  lastAttemptAt: number;
  now: number;
  cooldownMs: number;
  attempts?: number;
  maxAttempts?: number;
};

export type CloudSessionRestoreInput = {
  sessionAgentId?: string | null;
  currentAgentId?: string | null;
  runtimeConnected: boolean;
  runtimePresetId?: string | null;
  acpSessionId?: string | null;
};

export type CloudSessionRestorePlan = {
  agentId: string | null;
  needsEnsure: boolean;
  resumeSessionId: string | null;
};

export type OnboardingBootRetryInput = {
  appView: string;
  hasSelection: boolean;
  bootError: string | null | undefined;
  attempts: number;
  maxAttempts: number;
};

export type OnboardingBootRecoveryInput = {
  appView: string;
  expectedAgentId?: string | null;
  runtimeStatus?: string | null;
  runtimeConnected: boolean;
  runtimePresetId?: string | null;
};

export type InFlightServerAgentStatusInput = {
  presetMatches: boolean;
  status: string | null | undefined;
  isRunning: boolean;
};

export type HarnessInstallDecisionInput = {
  harnessStatus: "not_installed" | "installed" | "running" | "error" | null;
};

export type BootInstallProgressVisibilityInput = {
  stage: ReconnectBootStage | "creating_workspace" | "waiting_for_workspace" | "installing_starter_project" | null;
  installStep: "checking" | "installing" | "authenticating" | "verifying" | "complete" | "error" | null;
};

export type WorkspaceBootOverlayInput = {
  appView: "onboarding" | "booting" | "app";
  runtimeStatus: string | null | undefined;
  runtimeStage: string | null | undefined;
  terminalAuthActive: boolean;
};

export type ReconnectBootStage =
  | "waiting_for_workspace"
  | "waiting_for_terminal"
  | "installing_agent"
  | "starting_agent";

export type ReconnectBootStageInput = {
  workspaceHydrated: boolean;
  hasWorkspace: boolean;
  terminalStatus: "disconnected" | "connecting" | "connected" | "error";
  agentStatus: string;
  isTerminalAuth: boolean;
};

export type CloudInitialShellInput = {
  savedSetupComplete: boolean;
  savedAgentId?: string | null;
  savedWorkingDirectory?: string | null;
};

export type CloudInitialShellState = {
  setupComplete: boolean;
  appView: "app" | "onboarding";
};

export type WorkspacePersistenceInput = {
  appView: "onboarding" | "booting" | "app";
  disconnectPending: boolean;
  workspace: {
    provider: "sprites" | "gcp_compute";
    resourceId: string;
  } | null;
};

export type WorkspacePersistencePlan =
  | {
    mode: "persist";
    provider: "sprites" | "gcp_compute";
    workspaceId: string;
  }
  | {
    mode: "clear";
  }
  | {
    mode: "skip";
  };

export function deriveInitialCloudShellState(
  input: CloudInitialShellInput,
): CloudInitialShellState {
  const resumeInShell =
    input.savedSetupComplete
    && Boolean(input.savedAgentId)
    && Boolean(input.savedWorkingDirectory);

  return {
    setupComplete: resumeInShell,
    appView: resumeInShell ? "app" : "onboarding",
  };
}

export function deriveWorkspacePersistencePlan(
  input: WorkspacePersistenceInput,
): WorkspacePersistencePlan {
  if (input.disconnectPending || input.appView === "onboarding") {
    return { mode: "clear" };
  }

  if (!input.workspace) {
    return { mode: "skip" };
  }

  return {
    mode: "persist",
    provider: input.workspace.provider,
    workspaceId: input.workspace.resourceId,
  };
}

export function deriveReconnectBootStage(
  input: ReconnectBootStageInput,
): ReconnectBootStage | null {
  if (!input.workspaceHydrated || !input.hasWorkspace) {
    return "waiting_for_workspace";
  }

  if (input.agentStatus === "installing") {
    return "installing_agent";
  }

  if (input.isTerminalAuth) {
    return null;
  }

  if (input.agentStatus === "authenticating") {
    return "starting_agent";
  }

  if (input.agentStatus === "ready") {
    return null;
  }

  if (input.terminalStatus !== "connected") {
    return "waiting_for_terminal";
  }

  if (
    input.agentStatus === "connecting"
    || input.agentStatus === "disconnected"
  ) {
    return "starting_agent";
  }

  return null;
}

export function shouldAttemptCloudAgentAutoStart(input: CloudAutoStartInput): boolean {
  if (input.loading) return false;
  if (input.appView !== "app") return false;
  if (!input.hasWorkspace) return false;
  if (!input.selectedAgent) return false;
  if (input.maxAttempts !== undefined && (input.attempts || 0) >= input.maxAttempts) return false;

  const blockedStatuses = new Set(["connecting", "installing"]);
  if (blockedStatuses.has(input.agentStatus) || input.agentStatus.startsWith("authenticating")) return false;

  return input.now - input.lastAttemptAt >= input.cooldownMs;
}

export function buildCloudSessionRestorePlan(
  input: CloudSessionRestoreInput,
): CloudSessionRestorePlan {
  const agentId = input.sessionAgentId && input.sessionAgentId !== "unknown"
    ? input.sessionAgentId
    : input.currentAgentId || null;

  if (!agentId) {
    return {
      agentId: null,
      needsEnsure: false,
      resumeSessionId: input.acpSessionId || null,
    };
  }

  const needsEnsure = !input.runtimeConnected || input.runtimePresetId !== agentId;
  return {
    agentId,
    needsEnsure,
    resumeSessionId: input.acpSessionId || null,
  };
}

export function isTransientOnboardingBootError(error: string | null | undefined): boolean {
  return false;
}

export function shouldReuseInFlightServerAgentStatus(
  input: InFlightServerAgentStatusInput,
): boolean {
  if (!input.presetMatches) return false;
  if (!input.isRunning) return false;
  return input.status === "authenticating"
    || input.status === "connecting"
    || input.status === "installing";
}

export function shouldAutoRetryOnboardingBoot(input: OnboardingBootRetryInput): boolean {
  if (input.appView !== "booting") return false;
  if (!input.hasSelection) return false;
  if (!isTransientOnboardingBootError(input.bootError)) return false;
  return input.attempts < input.maxAttempts;
}

export function shouldRecoverBootIntoApp(input: OnboardingBootRecoveryInput): boolean {
  if (input.appView !== "booting") return false;
  if (!input.runtimeConnected) return false;
  if (input.runtimeStatus !== "ready") return false;
  if (input.expectedAgentId && input.runtimePresetId && input.expectedAgentId !== input.runtimePresetId) {
    return false;
  }
  return true;
}

export function shouldInstallHarness(input: HarnessInstallDecisionInput): boolean {
  return input.harnessStatus !== "installed" && input.harnessStatus !== "running";
}

export function shouldShowBootInstallProgress(input: BootInstallProgressVisibilityInput): boolean {
  if (input.stage !== "installing_agent") {
    return false;
  }

  return input.installStep !== null && input.installStep !== "complete" && input.installStep !== "error";
}

/**
 * Terminal-based agent authentication is a usable workspace state: the shell
 * and terminal must remain reachable so the user can complete the login. A
 * runtime `auth_required` state therefore must not keep the boot screen over
 * the app once a terminal-auth challenge is active.
 */
export function shouldShowWorkspaceBootOverlay(input: WorkspaceBootOverlayInput): boolean {
  if (input.terminalAuthActive) return false;
  if (input.runtimeStatus === "ready" || input.runtimeStatus === "idle") return false;
  if (input.runtimeStatus === "booting" || input.runtimeStatus === "error") return true;
  return input.runtimeStatus === "auth_required" && input.appView === "booting" && Boolean(input.runtimeStage);
}
